import { Group, Indicator, NumberFormatter, Text } from '@mantine/core';
import { IconGavel, IconPhoneRinging, IconReportMoney, IconScale } from '@tabler/icons-react';
import { createColumnHelper } from '@tanstack/react-table';
import { useGetDebtor } from 'api/index';
import dayjs from 'dayjs';
import { i18nKeys, tKeysLoose } from 'locales/index';
import { useTranslation } from 'locales/logic';
import type { LightThirdPartyCase } from 'modules/ThirdpartyCases/api';
import { useParams } from 'react-router';
import { isNullish } from 'remeda';
import TpCaseStatusItem from 'shared/components/TpCaseStatusItem';
import { getCurrencySymbolFromCurrencyCode } from 'shared/utils/currency';

const columnHelper = createColumnHelper<LightThirdPartyCase>();

export const useBuildThirdpartyCaseTableColumns = () => {
  const { t } = useTranslation();
  const { id: debtorId } = useParams<{ id: string }>();

  const { data: debtorDetailsResponse } = useGetDebtor(debtorId);

  if (isNullish(debtorDetailsResponse)) return [];

  return [
    columnHelper.accessor('case_type', {
      header: t(i18nKeys.CASE_TYPE),
      cell: info => {
        const caseDetail = info.row.original;
        return (
          <Group align="center" wrap="nowrap">
            <Indicator color="red" size={5} disabled={!caseDetail?.conversation?.unread} zIndex={1}>
              {getThirdpartyCaseIcon(caseDetail.case_type)}
            </Indicator>
            <Text size="sm">
              {t(tKeysLoose.THIRD_PARTY_CASES.CASE_TYPE[info.getValue().toUpperCase()])}
            </Text>
          </Group>
        );
      },
    }),
    columnHelper.accessor('case_id', {
      header: t(i18nKeys.FORM.REFERENCE),
      cell: info => (
        <Text size="sm" truncate="end">
          {info.getValue()}
        </Text>
      ),
    }),
    columnHelper.accessor('created_at', {
      header: t(i18nKeys.CREATION_DATE),
      cell: info => <Text size="sm">{dayjs(info.getValue()).format('L')}</Text>,
    }),
    columnHelper.accessor('last_update', {
      header: t(i18nKeys.LAST_UPDATE),
      cell: info => <Text size="sm">{dayjs(info.getValue()).format('L LT')}</Text>,
    }),
    columnHelper.accessor(row => (row.closed ? 'closed' : row.started ? 'started' : 'pending'), {
      id: 'status',
      header: t(i18nKeys.STATUS),
      cell: info => <TpCaseStatusItem caseDetail={info.row.original} />,
    }),
    columnHelper.accessor('total_tvac', {
      header: t(i18nKeys.TOTAL_VAT_PRICE),
      cell: info => (
        <NumberFormatter
          thousandSeparator=" "
          value={Number(info.getValue())}
          decimalScale={2}
          suffix={` ${getCurrencySymbolFromCurrencyCode(debtorDetailsResponse.data.attributes.currency)}`}
        />
      ),
    }),
    columnHelper.accessor('total_remaining', {
      header: t(i18nKeys.BALANCE),
      cell: info => (
        <NumberFormatter
          thousandSeparator=" "
          value={Number(info.getValue())}
          decimalScale={2}
          suffix={` ${getCurrencySymbolFromCurrencyCode(debtorDetailsResponse.data.attributes.currency)}`}
        />
      ),
    }),
  ];
};

const getThirdpartyCaseIcon = (caseType: string) => {
  switch (caseType) {
    case 'lawyer':
      return <IconGavel size={20} strokeWidth={1.5} />;
    case 'bailiff':
      return <IconScale size={20} strokeWidth={1.5} />;
    case 'debt_collector':
      return <IconReportMoney size={20} strokeWidth={1.5} />;
    default:
      return <IconPhoneRinging size={20} strokeWidth={1.5} />;
  }
};
